/**
 * Figure S6's "Inbound to triage" card — the middle of the Portfolio
 * Overview's right rail. The disclosures that arrived and have not been
 * reviewed, newest first, each a link straight into Inbound.
 *
 * The count in the pill and the rows below it are the SAME set, so the number
 * a portfolio reads is always the number of rows it then works through.
 */
import { Inbox } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { AggregatorProject } from '../../types'

/**
 * `match_computed_at` is stamped on every portfolio row by
 * crosspollination/handler.py and comes through the portfolio route raw
 * (measured live on the dev estate, 6 Sep 2026). It is declared here rather
 * than in the shared types file so this lane does not edit a file three other
 * lanes are editing in the same build.
 */
export type InboundProject = AggregatorProject & { match_computed_at?: string }

export interface InboundToTriageCardProps {
  /** Every unreviewed project in the portfolio payload. */
  projects: InboundProject[]
  /** Rows to list; the pill always states the full count. */
  limit?: number
  now?: number
}

/** Was this disclosure scored today? Only then does the figure's caption apply. */
export function scoredToday(project: InboundProject, now: number = Date.now()): boolean {
  const stamp = project.match_computed_at ?? project.created_at
  if (!stamp) return false
  const t = new Date(stamp)
  if (Number.isNaN(t.getTime())) return false
  const today = new Date(now)
  return (
    t.getFullYear() === today.getFullYear() &&
    t.getMonth() === today.getMonth() &&
    t.getDate() === today.getDate()
  )
}

export function scoredTodayCount(projects: InboundProject[], now: number = Date.now()): number {
  return projects.filter((p) => scoredToday(p, now)).length
}

/**
 * WHEN this disclosure was scored, in the figure's own shape. The figure reads
 * "scored today" because every row in it was; a real portfolio's queue is
 * mostly older than that, and the row still has to say something true. So:
 * "scored today", "scored 5 Sep", or — for a disclosure the matcher has not
 * reached — "not scored yet". Never silence, and never "today" for a stamp
 * that is not today.
 */
export function scoredLine(project: InboundProject, now: number = Date.now()): string {
  if (!project.match_computed_at) return 'not scored yet'
  const t = new Date(project.match_computed_at)
  if (Number.isNaN(t.getTime())) return 'not scored yet'
  if (scoredToday(project, now)) return 'scored today'
  return `scored ${t.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`
}

/**
 * The pill beside the heading. The figure shows "3 today" because its queue
 * arrived that day; when nothing was scored today the honest pill is the size
 * of the queue itself, which is the number the reader is about to work through.
 */
export function queuePill(projects: InboundProject[], now: number = Date.now()): string | null {
  if (projects.length === 0) return null
  const today = scoredTodayCount(projects, now)
  return today > 0 ? `${today} today` : `${projects.length} waiting`
}

const DEFAULT_LIMIT = 3

export default function InboundToTriageCard({
  projects,
  limit = DEFAULT_LIMIT,
  now = Date.now(),
}: InboundToTriageCardProps) {
  const ordered = [...projects].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )
  const shown = ordered.slice(0, limit)
  const pill = queuePill(projects, now)

  return (
    <div data-part="InboundToTriageCard" className="light-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-[15px] font-bold text-iflow-900">
          <Inbox size={16} className="text-iflow-500" aria-hidden /> Inbound to triage
        </h3>
        {pill && (
          <span
            data-testid="inbound-queue-pill"
            className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11.5px] font-semibold text-amber-800"
          >
            {pill}
          </span>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="mt-2 text-[12.5px] text-gray-600">
          Nothing is waiting to be triaged. New inbound appears here as it arrives.
        </p>
      ) : (
        <ul className="mt-2">
          {shown.map((project) => (
            <li
              key={project.project_id}
              data-testid="inbound-triage-row"
              className="flex items-start justify-between gap-2 border-t border-iflow-900/5 py-2 first:border-t-0"
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold text-iflow-900">{project.name}</div>
                <div className="text-[11.5px] text-gray-500">
                  {project.entity_name ?? 'Unnamed entity'} · {scoredLine(project, now)}
                </div>
              </div>
              <Link
                to="/agg/inbound"
                className="shrink-0 text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline"
              >
                Open →
              </Link>
            </li>
          ))}
        </ul>
      )}

      {ordered.length > shown.length && (
        <div className="mt-2 text-right">
          <Link to="/agg/inbound" className="text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline">
            All {ordered.length} in Inbound →
          </Link>
        </div>
      )}
    </div>
  )
}
